<template>
  <ModalBase show-modal :auto-h="true" @close-modal="close" class="gk-modal-recurrent-invoice">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-slot:pretitle>
          {{ (isEdit) ? $gettext('EDIT'): $gettext('ADD') }}
        </template>
        <template v-slot:title>
          {{ $gettext('Schedule Invoice') }}
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <b-form v-if="!loading" ref="recurrent-task-form" @submit="onSubmit">
        <p v-if="!currentCompany.is_premium" class="text-danger">
          {{ $gettext('To save scheduled invoices, you have to be a Premium user.') }}
        </p>
        <!-- task -->
        <b-form-group id="recurrent-input-group-taskname" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-taskname">
          <template slot="label">
            <span v-translate>Task name</span>
          </template>
          <b-input-group>
            <b-form-input
              id="recurrent-input-taskname"
              v-model="recurrent.name"
              required
            />
          </b-input-group>
        </b-form-group>


        <!-- recipients -->
        <b-form-group id="recurrent-input-group-emails" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-emails">
          <template slot="label">
            <span v-translate>Recipients</span>
          </template>
          <b-input-group>
            <b-form-input
            id="recurrent-input-emails"
            v-model="recurrent.emails"
            placeholder="jane@example.com, fred@example.com"
            required
          />
          </b-input-group>
        </b-form-group>

        <!-- message -->
        <b-form-group id="recurrent-input-group-comments" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-comments">
          <template slot="label">
            <span v-translate>Email Message</span>
          </template>
          <b-input-group>
            <b-form-textarea
              id="recurrent-input-comments"
              v-model="recurrent.comments"
              :placeholder="$gettext('Dear ....')"
              rows="3" max-rows="6"
            />
          </b-input-group>
        </b-form-group>

        <!-- start date -->
        <b-form-group v-if="!isEdit" id="recurrent-input-group-start_date" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-start_date">
          <template slot="label">
            <span v-translate>Start date</span>
          </template>
          <b-input-group>
            <b-form-datepicker 
              id="recurrent-input-start_date"
              v-model="recurrent.start_date"
              :locale="locale"
            />
          </b-input-group>
        </b-form-group>

        <!-- frequency -->
        <b-form-row>
          <b-col cols="12" lg="6">
            <b-form-group id="recurrent-input-group-interval" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-interval">
              <template slot="label">
                <span v-translate>Frequency</span>
              </template>
              <b-input-group>
                <b-form-select id="recurrent-input-interval" v-model="recurrent.interval" :options="interval" required />
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col cols="12" lg="6">
            <b-form-group id="recurrent-input-group-frequency" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-frequency">
              <template slot="label">
                <span>{{ frequencyLabelText }}</span>
              </template>
              <b-input-group>
                <!-- weeks -->
                <b-form-select v-if="recurrent.interval === 'weekly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                  <b-form-select-option v-for="idx in 3" :key="'weekly-' + idx" :value="idx">
                    {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('weeks') : $gettext('week') }}
                  </b-form-select-option>
                </b-form-select>

                <!-- months -->
                <b-form-select v-if="recurrent.interval === 'monthly'" id="recurrent-input-frequency" v-model="recurrent.skip">
                  <b-form-select-option v-for="idx in 12" :key="'monthly-' + idx" :value="idx">
                    {{ ((idx > 1) ? idx : '') }} {{ (idx > 1) ? $gettext('months') : $gettext('month') }}
                  </b-form-select-option>
                </b-form-select>

                <!-- weekdays -->
                <b-form-checkbox-group
                  v-if="recurrent.interval === 'daily'"
                  id="recurrent-input-frequency"
                  v-model="recurrent.days"
                  :options="daily_options"
                  name="frequency-daily"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-form-row>


        <!-- end -->
        <b-form-group id="recurrent-input-group-ends" label-class="gk-label-margin gk-modal-label" label-for="recurrent-input-ends">
          <template slot="label">
            <span v-translate>Ends</span>
          </template>

          <b-input-group style="flex-direction: column;">
              
            <!-- never -->
            <b-form-row class="mb-2">
              <b-col cols="12">
                <b-form-radio id="recurrent-input-end-method" v-model="recurrent.end_method" name="end-method" value="endless" class="pt-2">{{ $gettext('never') }}</b-form-radio>
              </b-col>
            </b-form-row>
            
            <!-- after -->
            <b-form-row class="mb-2">
              <b-col cols="3">
                <b-form-radio v-model="recurrent.end_method" name="end-method" value="iteration" class="pt-2">{{ $gettext('after') }}</b-form-radio>
              </b-col>
              <b-col cols="9">

                <b-form-row :class="{ 'opacity-25': recurrent.end_method !== 'iteration' }">
                  <b-col cols="6">
                    <b-form-input
                      id="recurrent-input-iterations"
                      v-model="recurrent.iterations"
                      :disabled="recurrent.end_method !== 'iteration'"
                      min="1" value="1" step="1"
                      type="number"
                    />
                  </b-col>
                  <b-col cols="6">
                    <label for="recurrent-input-iterations">{{ $gettext('times') }}</label>
                  </b-col>
                </b-form-row>
      
              </b-col>
            </b-form-row>

            <!-- on -->
            <b-form-row>
              <b-col cols="3">
                <b-form-radio v-model="recurrent.end_method" name="end-method" value="date" class="pt-2">{{ $gettext('on') }}</b-form-radio>
              </b-col>
              <b-col cols="9" :class="{ 'opacity-25': recurrent.end_method !== 'date' }">
                <b-form-datepicker 
                  id="recurrent-input-end_date"
                  :disabled="recurrent.end_method !== 'date'"
                  v-model="recurrent.end_date"
                  :locale="locale"
                  :min="recurrent.start_date"
                />
              </b-col>
            </b-form-row>
            
          </b-input-group>
        </b-form-group>

        <!-- summary / next delivery-->
        <b-form-group id="recurrent-input-group-summary" label-cols-sm="3" label-align-sm="right" content-cols-sm :label="summaryLabelText" label-for="recurrent-input-name">
          <p v-if="isEdit" class="text-brand">
            {{ nextDeliveryDate(recurrent) }}
            <br>
            {{ recurrencySummary(recurrent) }}
          </p>
          <p class="text-blue" v-else v-html="createSummary(recurrent)" />
        </b-form-group>

      </b-form>
    </template>
    <template v-slot:actions>
      <div class="d-flex w-100 justify-content-between">
        <div>
          <GekkoButton
            v-if="!isEdit"
            :text="$gettext('Cancel')"
            variant="link"
            class="text-danger"
            @click="resetRecurrent()"
          />
          <GekkoButton
            v-else
            :text="$gettext('Close')"
            variant="light"
            @click="close()"
          />
        </div>
        <div class="d-flex" style="gap: 0.5rem;">
          <GekkoButton
            v-if="isEdit"
            :text="$gettext('Update')"
            @click="prepareAndUpdate()"
          />
          <GekkoButton
            v-if="!isEdit"
            :text="$gettext('Save')"
            @click="prepareAndSave()"
          />
        </div>
      </div>
    </template>
  </ModalBase>
</template>

<script>
import moment from 'moment'

import { mapGetters, mapActions, mapMutations } from "vuex";
import recurrentInvoices from "@/mixins/recurrent.invoices";

import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';

import ModalBase from "@/components/base_components/ModalBase"
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import AddProjectSimple from "@/components/contacts/AddProjectSimple"
import CustomerSelect from "@/components/invoices/ui/CustomerSelect"

export default {
  name: 'RecurrentInvoiceModal',
  mixins: [recurrentInvoices],
  components: { 
    ModalBase,
    TitleBar,
    GekkoButton,
    AddProjectSimple,
    CustomerSelect
  },
  data () {
    return {
      loading: true,
      recurrent: {},
    }
  },
  computed: {
    ...mapGetters ({ 
      currentCompany: "main/getCurrentCompany",
      storeRecurrent: "invoices/getRecurrentInvoice",
      invoiceDate: "invoicesRecurrent/getInvoiceDate",
      invoice: "invoices/getInvoice",
      defaultRecurrent: "invoicesRecurrent/getDefaultRecurrent",
      recurrentBackup: "invoices/getRecurrentInvoiceBackup",
      scheduledForToday: "invoices/getScheduledForToday"
    }),
    isEdit () {
      return this.storeRecurrent != null
    },
    frequencyLabelText () {
      let labelText = ''
      if (this.recurrent.interval === 'daily') {
        labelText = this.$gettext('on')
      } else {
        labelText = this.$gettext('every')
      }
      return labelText
    },
    summaryLabelText () {
      let labelText = ''
      if (!this.isEdit) {
        labelText = this.$gettext('Summary')
      } else {
        labelText = this.$gettext('Next Delivery')
      }

      return labelText
    },
    locale () {
      return window.preferred_language;
    }
  },
  watch: {
    'recurrent.start_date' () {
      this.setNextDeliveryMsg()
    },
    'recurrent.days' () {
      if (!this.isEdit) {
        this.setNextDeliveryMsg()
      }
    },
    'recurrent.end_method' (current_end_method) {
      switch (current_end_method) {
        case 'iteration':
          this.calculateEnddateFromIteration()
          break;
        case 'date':
          this.calculateIterationFromEnddate()
          break;
      }
    },
    'recurrent.iterations' (iterations) {
      this.calculateEnddateFromIteration()
    },
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    ...mapMutations({
      toggleModal: 'invoicesRecurrent/TOGGLE_MODAL',
      setRecurrent: 'invoices/SET_RECURRENT_INVOICE',
      setSummary: 'invoicesRecurrent/SET_SUMMARY'
    }),
    ...mapActions({
      saveRecurrent: 'invoicesRecurrent/saveRecurrent'
    }),
    close () {
      document.body.classList.remove('modal-on')
      this.toggleModal()
    },
    setupData () {
      console.log('recurrentModal setup. Is edit: ', this.isEdit)
      if (this.isEdit || this.recurrentBackup) {
        
        if (!this.isEdit && this.recurrentBackup) {
          console.log('recurrentBackup', this.recurrentBackup)
          this.recurrent = this.recurrentBackup
        } else {
          console.log('storeRecurrent', this.storeRecurrent)
          this.recurrent = this.storeRecurrent
        }

        // set end_date as (today + 30 days) end_date if end_method !== 'date' 
        if (this.recurrent.end_method !== 'date') {
          let end_date = moment().add(1, 'months').format('YYYY-MM-DD')
          this.recurrent.end_date = end_date
        }
        // nullify iterations if end_method !== 'iteration' 
        if (this.recurrent.end_method !== 'iteration') {
          this.recurrent.iterations = 1
        }
        // reset days to default all checked if interval !== 'daily'
        if (this.recurrent.interval !== 'daily') {
          this.recurrent.days = this.daily_options.map(option => option.value)
        } 

      } else {
        // CREATE SETUP - DEFAULT DATA
        this.recurrent = this.defaultRecurrent
        let invoice_date = (this.invoice && this.invoice.invoice_date) ? this.invoice.invoice_date : this.invoiceDate

        // setting suggested start date as today (Feedback 17-07)
        invoice_date = new Date().format('Y-m-d')

        this.$set(this.recurrent, 'start_date', invoice_date)
        this.$set(this.recurrent, 'iterations', 1)
        this.$set(this.recurrent, 'company_profile', this.currentCompany.id)
        if (this.currentCompany.companysettings.default_invoice_email_message) {
          this.$set(this.recurrent, 'comments', this.currentCompany.companysettings.default_invoice_email_message)
        }
        if (this.invoice.recipient_customer && this.invoice.recipient_customer.email) {
          this.$set(this.recurrent, 'emails', this.invoice.recipient_customer.email)
        }

        this.setNextDeliveryMsg()
      }
      // make summary available on store
      this.setSummary(this.summaryLabelText + ' ' + this.nextDeliveryDate(this.recurrent) + ' ' + this.recurrencySummary(this.recurrent))
      this.loading = false
    },
    onSubmit(event) {
      event.preventDefault()
    },
    openViewInvoice () {
      this.close()
      this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
    },
    checkValidation () {
      return this.$refs['recurrent-task-form'].reportValidity()
    },
    prepareAndUpdate () {
      if (this.checkValidation()) {
        const update_data = this.prepareDataForSubmit(this.recurrent)
        InvoiceRecurrentService.update(update_data.id, update_data).then((response) => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice Updated'),
            text: this.$gettext('Recurrent invoice updated successfully.'),
            variant: 'success',
            duration: 4
          })
          this.setSummary(this.summaryLabelText + ' ' + this.nextDeliveryDate(this.recurrent) + ' ' + this.recurrencySummary(this.recurrent))

          console.log('prepareAndUpdate setRecurrent', update_data)

          // update store data
          this.setRecurrent(this.recurrent)

          // this.recurrent = this.storeRecurrent

          this.$emit('updated-invoice')
          this.close()
        })
      }
    },
    prepareAndSave () {
      if (!this.currentCompany.is_premium) {
        this.close()
        this.$store.commit('togglePremiumModal', 'web_invoices_recurrent')
      } else {
        if (this.checkValidation() && this.invoice && this.invoice.id) {

          const prepared_recurrent = JSON.parse(JSON.stringify(this.recurrent))
          prepared_recurrent.invoice = this.invoice.id

          this.setSummary(this.summaryLabelText + ' ' + this.createSummary(this.recurrent))

          this._createRecurrentTask(prepared_recurrent).then((response) => {
            console.log('_createRecurrentTask then response', response)
            console.log('_createRecurrentTask then this.recurrent', this.recurrent)
            
            if (this.scheduledForToday) {
              this.$router.push({ name: 'InvoiceSendRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
            } else {
              this.$router.push({ name: 'InvoiceViewRecurrent', params: { invoiceId: this.recurrent.id } }).catch(() => {})
            }
            this.close()

          }).catch((error) => {
            console.log('error', error)
          })

        }
      }
    },
    _createRecurrentTask (recurrent_data) {
      return this.saveRecurrent(this.prepareDataForSubmit(recurrent_data)).then((recurrentResponse) => {
        this.recurrent.id = recurrentResponse.id

        // update store data
        this.setRecurrent(this.recurrent)

        this.$store.commit('invoices/REFRESH_INVOICE_RECURRENT_LIST')
        return recurrentResponse
      })
    },
    resetRecurrent () {
      this.setRecurrent(null)
      this.setSummary(null)
      this.setupData()
      this.close()
    },
    calculateEnddateFromIteration () {
      if (this.recurrent.start_date) {
        const new_date = new Date(this.recurrent.start_date)

        if (this.recurrent.interval === 'daily') {
          const days = this.recurrent.days

          let weeks = Math.floor(this.recurrent.iterations / days.length)
          if ((this.recurrent.iterations % days.length) === 0) weeks--

          const extraDays = this.recurrent.iterations - days.length * weeks

          let today_day = new Date(this.recurrent.start_date).getDay() - 1; // -1 is sunday
          if (today_day === -1) today_day = 6
          
          var c = 0, cnt = 0;
          for (var i = 0; i < 7; i++) {
            c++;
            if (days.indexOf(today_day.toString()) !== -1) {
              cnt++;
              if (cnt === extraDays) break
            }
            today_day++
            if (today_day === 7) today_day = 0
          }
          new_date.setDate(new_date.getDate() + (weeks) * 7 + c - 1)
        }
        if (this.recurrent.interval === 'weekly') {
          new_date.setDate(new_date.getDate() + this.recurrent.skip * (this.recurrent.iterations - 1) * 7)
        }
        if (this.recurrent.interval === 'monthly') {
          new_date.addMonths((this.recurrent.iterations - 1) * this.recurrent.skip)
        }
        this.recurrent.end_date = new_date.format('Y-m-d')
      }
    },
    calculateIterationFromEnddate () {
      var date_start_date = new Date(this.recurrent.start_date);
      var date_end_date = new Date(this.recurrent.end_date);
      if (this.recurrent.start_date) {
        if (date_start_date > date_end_date) {
          this.recurrent.end_date = this.recurrent.start_date;
          date_end_date = date_start_date;
        }
      }

      if (this.recurrent.interval === 'daily') {
        const weeksInBetween = Math.floor((date_end_date - date_start_date) / 1000 / 3600 / 24 + 1) / 7;
        const extraDays = Math.round((weeksInBetween - Math.trunc(weeksInBetween)) * 7);
        const nrOfWeeks = Math.trunc(weeksInBetween);
        const days = this.recurrent.days;
        this.recurrent.iterations = days.length * nrOfWeeks;

        var today_day = date_start_date.getDay() - 1; // -1 is sunday
        if (today_day === -1) today_day = 6

        var c= 0, cnt = 0;
        for (var i = 0; i < extraDays; i++) {
          if (days.indexOf(today_day.toString()) !== -1) {
            c = c + 1;
            cnt = cnt + 1;
            if (cnt === extraDays) break
          }
          today_day++;
          if (today_day === 7) today_day = 0
        }
        this.recurrent.iterations = this.recurrent.iterations + c;
      }
      if (this.recurrent.interval === 'weekly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 7 / this.recurrent.skip) + 1;
      }
      if (this.recurrent.interval === 'monthly') {
        this.recurrent.iterations = date_end_date - date_start_date;
        this.recurrent.iterations = Math.floor((this.recurrent.iterations / 1000 / 3600 / 24) / 30 / this.recurrent.skip) + 1;
      }
    },
    setNextDeliveryMsg () {
      let next_delivery_message = ''
      if (this.recurrent.interval === 'daily') {

        var today_day = new Date(this.recurrent.start_date).getDay() - 1; //-1 is sunday

        if (today_day == -1) {
          today_day = 6
        }
        var c = 0;
        for (var i = 0; i < 7; i++) {
          if (this.recurrent.days.indexOf(today_day.toString()) != -1) {
            break
          }
          today_day++
          if (today_day == 7) {
            today_day = 0
          }
          c++
        }

        var date_obj = new Date(this.recurrent.start_date)
        date_obj.setDate(date_obj.getDate() + c)

        this.$set(this.recurrent, 'start_date', date_obj.format('Y-m-d'))

        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          // next_delivery_message = this.$gettext('right now')
          next_delivery_message = ''
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      } else {
        if (this.recurrent.start_date == Date.today().format('Y-m-d')) {
          // next_delivery_message = this.$gettext('right now')
          next_delivery_message = ''
        } else {
          next_delivery_message = this.recurrent.start_date
        }
      }
      next_delivery_message = next_delivery_message + ' ' + this.$gettext('at 10 AM CET')
      this.$set(this.recurrent, 'next_delivery_message', next_delivery_message)
    },

  }
}
</script>

<style lang="scss">
.gk-modal-recurrent-invoice {
  .gk-modal {
    max-width: 450px !important;
    .action-buttons-wrapper {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
    }
    .form-row { align-items: baseline; }
    // .form-group label { font-size: 1rem; }
    #recurrent-input-group-comments {
      overflow-y: auto !important;
    }
    #recurrent-input-group-interval {
      .form-group { align-items: center; }
        #recurrent-input-frequency {
        .custom-control { width: 55px; }
      }
    }
    #recurrent-input-group-ends {
      margin-bottom: 2rem;
    }
    #recurrent-input-group-summary {
      font-size: 1rem;
      padding: 1rem 0;
      border: 1px solid $colorBrand;
      border-radius: $radiusLg;
    }
  }
}
</style>